import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import GameSkeleton from '@/components/GameSkeleton/GameSkeleton';
import GameplaySection from '@/components/GameplaySection/GameplaySection';
import NotFoundPage from '@/pages/NotFoundPage';
import { useQuizAdapter } from '@/game-engine/adapters/quizAdapter';
import { getQuizSchema } from '@/constants/pageMetadata';
import StructuredData from '@/components/StructuredData/StructuredData';
import { quizCompletionPreviewFixture } from '@/game-engine/completionPreviewFixtures';
import { useEmbedStyleOverridePreview } from '@/hooks/useEmbedStyleOverridePreview';
import { useEmbedCompletionContent } from '@/hooks/useEmbedCompletionContent';
import { getFullUrl } from '@/lib/utils/url';
import type { QuizMetadata } from '@/types/quiz';
import type { QuizQuestion } from '@/lib/services/questions';
import { useQuizQuestionsQuery } from '@/hooks/queries/useQuizQuestionsQuery';
import type { Question } from '@/lib/services/questions';
import type { UserProgress } from '@/game-engine/state-machine/gameTypes';
import type { PollOption } from '@/game-engine/question-items/types';
import { useBoardInviteJoinFlow } from '@/hooks/useBoardInviteJoinFlow';
import { useAuthId } from '@/hooks/useAuthId';
import { classifyEmailChoiceParam } from '@/lib/quiz/emailAnswerIntent';
import { useQuizEmailAnswerIntent } from '@/hooks/useQuizEmailAnswerIntent';
import { isEmbedEditorPreviewSearch } from '@/lib/embed/embedEditorPreview';
import { ActionableErrorState } from '@/components/errors/ActionableErrorState';
import { routes } from '@/routes';
import { useQuizNewsletterInvitation } from '@/features/newsletterInvitations';
import { preserveParentManagedIdentityOnInternalTarget } from '@/lib/auth/anonymousIdentity';
import type { QuizWebPreviewSnapshot } from '@/lib/embed/previewMessaging';
import { useQuizSponsorsStylePresetSession } from '@/hooks/useQuizSponsorsStylePresetSession';
import { getSponsorsStyleAttribution } from '@/lib/domain/sponsorsStylePreset';
import type { SponsorsStylePresetWebRuntime } from '@/lib/embed/sponsorsStylePresetRuntime';
import { buildSponsorsStylePresetWebRuntime } from '@/lib/embed/sponsorsStylePresetRuntime';
import { useActiveRunIdentity } from '@/lib/auth/activeRunIdentity';
import { useFontsReadyState, type FontsReadyState } from '@/hooks/useFontsReady';
import { resolveBrandThemeTokens } from '@/lib/embed/brandTheme';
import { extractPrimaryFontFamily } from '@/lib/embed/fontFamilies';
import { DEFAULT_BRAND_THEME_RECIPE_V2 } from '@/lib/embed/brandTheme';
import type { ResolvedQuizSponsorsStylePreset } from '@/lib/domain/sponsorsStylePreset';
import { trackBreadcrumb } from '@/lib/telemetry';
import { queueGTMEvent } from '@/lib/gtm';
import { isQuizPreviewQuestionPlaceholder } from '@/lib/quiz/quizPreviewQuestionPlaceholders';
import { getQuizGameplayModuleReadyAt, loadQuizGameplayModule } from './quizGameplayModule';

const QuizGameController = lazy(loadQuizGameplayModule);
const QuizCompletionSummary = lazy(
  () => import('@/components/QuizCompletionSummary/QuizCompletionSummary')
);

export type QuizPageProps = {
  initialQuiz?: QuizMetadata | null;
  initialQuestions?: QuizQuestion[];
  initialSponsorsStyle?: ResolvedQuizSponsorsStylePreset | null;
  demoTheme?: SponsorsStylePresetWebRuntime | null;
  playbackStartedAt?: number;
  playbackBootstrapReadyAt?: number;
  serverAnalyticsEnabled?: boolean;
  parentOwnedPreviewSnapshot?: QuizWebPreviewSnapshot | null;
  onPreviewReady?: () => void;
};

function PreviewReadySignal({ onReady }: { onReady: () => void }) {
  useEffect(() => onReady(), [onReady]);
  return null;
}

function isPollPreviewQuestion(question: QuizQuestion): boolean {
  return question.item_type === 'poll';
}

function buildPreviewSummaryQuestion(question: QuizQuestion): Question {
  return {
    ...question,
    stem: question.stem ?? '',
    key: question.key ?? '',
    distractor_1: question.distractor_1 ?? '',
    distractor_2: question.distractor_2 ?? '',
    distractor_3: question.distractor_3 ?? '',
    question_fact: question.question_fact ?? '',
    generation_tag_id: question.generation_tag_id ?? 0,
    breadcrumbs: question.breadcrumbs ?? null,
    source_url: question.source_url,
  };
}

function buildPreviewPollProgress(question: QuizQuestion): UserProgress {
  const pollOptions = Array.isArray(question.poll_options)
    ? ([...question.poll_options] as PollOption[]).sort(
        (left, right) => left.sort_index - right.sort_index
      )
    : [];
  const selectedOption = pollOptions[0];

  if (!selectedOption) {
    return { status: 'unanswered', guess_sequence: [] };
  }

  return {
    status: 'answered_correctly',
    guess_sequence: [],
    poll_response: {
      selectedOptionId: selectedOption.id,
      distribution: pollOptions.map((option, index) => ({
        optionId: option.id,
        label: option.label,
        sortIndex: option.sort_index,
        playerCount: index === 0 ? 3 : 1,
        totalCount: index === 0 ? 3 : 1,
      })),
    },
  };
}

function buildPreviewTriviaProgress(index: number): UserProgress {
  const mod = index % 3;
  if (mod === 0) return { status: 'answered_correctly', guess_sequence: [0] };
  if (mod === 1) return { status: 'answered_correctly', guess_sequence: [2, 0] };
  return { status: 'answered_incorrectly_in_progress', guess_sequence: [1, 3, 2, 1] };
}

function usePreviewNavigationBoundary(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    const openInNewTab = (element: HTMLAnchorElement | HTMLFormElement) => {
      element.target = '_blank';
      const rel = new Set(element.getAttribute('rel')?.split(/\s+/).filter(Boolean) ?? []);
      rel.add('noopener');
      rel.add('noreferrer');
      element.setAttribute('rel', [...rel].join(' '));
    };
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest('a[href]');
      if (link instanceof HTMLAnchorElement) openInNewTab(link);
    };
    const handleSubmit = (event: SubmitEvent) => {
      if (event.target instanceof HTMLFormElement) openInNewTab(event.target);
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
    };
  }, [enabled]);
}

const QuizPage: React.FC<QuizPageProps> = ({
  initialQuiz,
  initialQuestions,
  initialSponsorsStyle = null,
  demoTheme = null,
  playbackStartedAt,
  playbackBootstrapReadyAt,
  serverAnalyticsEnabled = true,
  parentOwnedPreviewSnapshot = null,
  onPreviewReady,
}) => {
  useBoardInviteJoinFlow();
  const [searchParams] = useSearchParams();
  const { slug = '' } = useParams<{ slug: string }>();
  const isEmbedEditorPreview = isEmbedEditorPreviewSearch(searchParams.toString());
  const isEmbeddedQuiz = searchParams.has('partner_id');
  const emailChoiceParam = searchParams.get('email_choice');
  const pollChoiceParam = searchParams.get('poll_choice');
  const emailChoiceClass = classifyEmailChoiceParam(emailChoiceParam);
  const hasEmailDeepLink = emailChoiceClass.kind !== 'none' || pollChoiceParam !== null;
  const hasParentOwnedPreview = parentOwnedPreviewSnapshot !== null;
  const effectiveSlug = parentOwnedPreviewSnapshot?.model.slug ?? slug;
  // The hydrating client loader has already replaced the prerendered SEO snapshot
  // with one current Live Edition snapshot before this page can render.
  const runtimeInitialQuiz = initialQuiz;
  const runtimeInitialQuestions = hasParentOwnedPreview
    ? parentOwnedPreviewSnapshot.model.questions
    : initialQuestions;
  const runtimeInitialSponsorsStyle = initialSponsorsStyle;
  const effectiveServerAnalyticsEnabled = serverAnalyticsEnabled && !isEmbedEditorPreview;
  const adapter = useQuizAdapter(effectiveSlug, {
    initialQuiz: runtimeInitialQuiz,
    initialQuestions: runtimeInitialQuestions,
    serverAnalyticsEnabled: effectiveServerAnalyticsEnabled,
    runtimeQuestionFetchEnabled: !isEmbedEditorPreview,
    authoritativeInitialData: hasParentOwnedPreview,
  });
  const newsletter = useQuizNewsletterInvitation(
    adapter.quiz?.partner_id,
    adapter.quiz?.quiz_id,
    !isEmbedEditorPreview && !hasParentOwnedPreview
  );
  const { isResolved: authResolved } = useAuthId();

  const {
    data: queriedQuizQuestions = [],
    isLoading: quizQuestionsLoading,
    isPending: quizQuestionsPending,
    isProgressReady,
    isProgressError,
  } = useQuizQuestionsQuery(adapter.quiz?.quiz_id, adapter.quiz?.edition_id, {
    // User-scoped progress must load before we hydrate the game when coming from email.
    initialData: runtimeInitialQuestions,
    // Authenticated Draft previews already use their dedicated membership contract.
    enabled: !isEmbedEditorPreview && !hasParentOwnedPreview,
  });
  const realQuizQuestions = useMemo(
    () => (hasParentOwnedPreview ? (runtimeInitialQuestions ?? []) : queriedQuizQuestions),
    [hasParentOwnedPreview, queriedQuizQuestions, runtimeInitialQuestions]
  );

  const quizQuestions =
    realQuizQuestions.length > 0
      ? realQuizQuestions
      : runtimeInitialQuestions?.length
        ? runtimeInitialQuestions
        : [];
  const hasInitialQuizQuestions = (runtimeInitialQuestions?.length ?? 0) > 0;
  const emailIntentQuestionsReady =
    quizQuestions.length > 0 &&
    (!quizQuestionsLoading || hasInitialQuizQuestions) &&
    (isProgressReady ?? !quizQuestionsPending);

  const emailIntent = useQuizEmailAnswerIntent({
    emailChoiceParam,
    pollChoiceParam,
    firstQuestion: quizQuestions[0],
    authResolved,
    questionsReady: emailIntentQuestionsReady,
  });

  const emailAnswerCanonicalIndex =
    emailIntent.status === 'ready' ? emailIntent.canonicalIndex : undefined;
  const emailPollOptionId = emailIntent.status === 'ready' ? emailIntent.pollOptionId : undefined;

  const runtimePresetQuizId =
    isEmbedEditorPreview || hasParentOwnedPreview ? null : adapter.quiz?.quiz_id;
  const presetSession = useQuizSponsorsStylePresetSession({
    quizId: runtimePresetQuizId,
    initialResolution: runtimeInitialSponsorsStyle,
  });
  const presetWebRuntime = useMemo(
    () =>
      demoTheme ??
      (presetSession.resolution
        ? buildSponsorsStylePresetWebRuntime(presetSession.resolution.configuration)
        : null),
    [demoTheme, presetSession.resolution]
  );
  const fontFamilies = useMemo(() => {
    if (!presetWebRuntime?.theme) return [];
    const appearance = presetWebRuntime.appearance === 'dark' ? 'dark' : 'light';
    const tokens = resolveBrandThemeTokens(presetWebRuntime.theme, appearance);
    return [
      extractPrimaryFontFamily(tokens['--font-family-title']),
      extractPrimaryFontFamily(tokens['--font-family-body']),
    ].filter((family): family is string => Boolean(family));
  }, [presetWebRuntime]);
  const fontsReadyState: FontsReadyState = useFontsReadyState(fontFamilies);
  const [firstFrameCommitted, setFirstFrameCommitted] = useState(false);
  const finalPresetWebRuntime = useMemo(() => {
    if (fontsReadyState !== 'fallback' || !presetWebRuntime?.theme) return presetWebRuntime;
    return {
      ...presetWebRuntime,
      theme: {
        ...presetWebRuntime.theme,
        fontPairing: DEFAULT_BRAND_THEME_RECIPE_V2.fontPairing,
        titleFontFamily: null,
        bodyFontFamily: null,
      },
      fontFaces: [],
    };
  }, [fontsReadyState, presetWebRuntime]);
  const receiver = useEmbedStyleOverridePreview({
    runtimeTheme:
      parentOwnedPreviewSnapshot || isEmbedEditorPreview ? undefined : finalPresetWebRuntime?.theme,
    runtimeFontFaces:
      parentOwnedPreviewSnapshot || isEmbedEditorPreview
        ? undefined
        : finalPresetWebRuntime?.fontFaces,
    receiverEnabled: isEmbedEditorPreview && !parentOwnedPreviewSnapshot,
    runtimeAppearance:
      parentOwnedPreviewSnapshot?.render.appearance ??
      (firstFrameCommitted ? finalPresetWebRuntime?.appearance : undefined),
    runtimePresentationReady: isEmbedEditorPreview || hasParentOwnedPreview || firstFrameCommitted,
  });
  const previewSurface =
    parentOwnedPreviewSnapshot?.render.previewSurface ?? receiver.previewSurface;
  const previewEmailSignup =
    parentOwnedPreviewSnapshot?.render.emailSignup ?? receiver.previewEmailSignup;
  const previewSponsorship =
    parentOwnedPreviewSnapshot?.render.sponsorship ?? receiver.previewSponsorship;
  const notifyPreviewContentReady =
    isEmbedEditorPreview || hasParentOwnedPreview
      ? (onPreviewReady ?? receiver.notifyPreviewContentReady)
      : undefined;
  const [machinePlayableReady, setMachinePlayableReady] = useState(false);
  const [machinePlayableFailed, setMachinePlayableFailed] = useState(false);
  const machinePlayableReadyAtRef = useRef<number | undefined>(undefined);
  const handlePlayableContentReady = useCallback(() => {
    machinePlayableReadyAtRef.current ??=
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    setMachinePlayableReady(true);
    notifyPreviewContentReady?.();
  }, [notifyPreviewContentReady]);
  const handlePlayableContentFailed = useCallback(() => {
    setMachinePlayableFailed(true);
  }, []);
  const {
    resolvedEmailSignupContent,
    resolvedSponsorshipContent,
    resolvedSponsorshipCtaContent,
    isPreviewMode,
  } = useEmbedCompletionContent({
    previewEmailSignup,
    previewSponsorship,
    effectiveConfiguration: presetSession.resolution?.configuration,
  });
  usePreviewNavigationBoundary(isPreviewMode);
  const sponsorsStyleAttribution = useMemo(
    () =>
      presetSession.resolution ? getSponsorsStyleAttribution(presetSession.resolution) : undefined,
    [presetSession.resolution]
  );

  // Build end-screen preview data from real questions when available
  const endScreenPreviewData = useMemo(() => {
    if (realQuizQuestions.length === 0) {
      return {
        questions: quizCompletionPreviewFixture.questions,
        userProgress: quizCompletionPreviewFixture.userProgress,
        adapterCompletion: quizCompletionPreviewFixture.adapterCompletion,
      };
    }

    const questions: Question[] = realQuizQuestions.map(buildPreviewSummaryQuestion);

    const userProgress: UserProgress[] = realQuizQuestions.map((question, index) =>
      isPollPreviewQuestion(question)
        ? buildPreviewPollProgress(question)
        : buildPreviewTriviaProgress(index)
    );

    const total = realQuizQuestions.length;
    const decorated = realQuizQuestions.map((_, i) => {
      const mod = i % 3;
      const attemptCount = mod === 0 ? 1 : mod === 1 ? 2 : 4;
      const eventuallyCorrect = mod !== 2;
      const emoji = !eventuallyCorrect
        ? '❌'
        : attemptCount === 1
          ? '✅'
          : attemptCount === 2
            ? '2️⃣'
            : '❌';
      return { attemptCount, emoji };
    });

    const totalAttempts = decorated.reduce((sum, item) => sum + item.attemptCount, 0);
    const rawAccuracy =
      total === 0 ? 0 : Math.round((1 - Math.max(0, totalAttempts - total) / (total * 3)) * 100);

    const adapterCompletion = {
      emojiResults: decorated.map((item) => item.emoji).join(''),
      accuracy: Math.max(0, Math.min(100, rawAccuracy)),
      totalQuestions: total,
      xpEarned: quizCompletionPreviewFixture.adapterCompletion.xpEarned,
    };

    return { questions, userProgress, adapterCompletion };
  }, [realQuizQuestions]);

  const title = adapter.quiz?.title ?? 'Quiz';
  const description = `Play the ${title} quiz on Atrivial`;
  const activeRunIdentity = useActiveRunIdentity();
  const requiresWritableIdentity = adapter.mode === 'quiz' && adapter.canPersistCompletion === true;
  const identityReady = !requiresWritableIdentity || activeRunIdentity.status === 'ready';
  const identityResolutionFailed =
    requiresWritableIdentity && activeRunIdentity.status === 'blocked';
  const progressReady =
    hasParentOwnedPreview || (isProgressReady ?? (!quizQuestionsLoading && !quizQuestionsPending));
  const shouldDelayGame = adapter.mode === 'quiz' && !adapter.quiz?.id;
  const shouldWaitForEmailIntent = emailIntent.status === 'pending';
  const shouldWaitForPreset =
    Boolean(runtimePresetQuizId) && !presetSession.resolution && presetSession.status !== 'failed';
  const shouldWaitForProgress = !progressReady;
  const shouldWaitForIdentity = !identityReady;
  const shouldWaitForFonts = fontsReadyState === 'pending';
  const presetResolutionFailed =
    Boolean(runtimePresetQuizId) && !presetSession.resolution && presetSession.status === 'failed';
  const progressResolutionFailed = Boolean(isProgressError) && !firstFrameCommitted;
  const firstFrameDataReady =
    !shouldDelayGame &&
    !shouldWaitForEmailIntent &&
    !shouldWaitForPreset &&
    !shouldWaitForProgress &&
    !shouldWaitForIdentity &&
    !shouldWaitForFonts &&
    quizQuestions.length > 0;
  const hasGenerationPreviewContent =
    hasParentOwnedPreview &&
    parentOwnedPreviewSnapshot.model.questions.some(isQuizPreviewQuestionPlaceholder);
  const shouldMountGame = firstFrameCommitted || firstFrameDataReady;
  const firstFrameReady =
    firstFrameDataReady && (machinePlayableReady || hasGenerationPreviewContent);
  const firstFrameStartRef = useRef<number>(
    playbackStartedAt ?? (typeof performance !== 'undefined' ? performance.now() : Date.now())
  );
  const hasMeasuredFirstFrameRef = useRef(false);
  const progressReadyAtRef = useRef<number | undefined>(undefined);
  const identityReadyAtRef = useRef<number | undefined>(undefined);
  const fontsReadyAtRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (progressReady && progressReadyAtRef.current === undefined) {
      progressReadyAtRef.current =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
    }
  }, [progressReady]);

  useEffect(() => {
    if (identityReady && identityReadyAtRef.current === undefined) {
      identityReadyAtRef.current =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
    }
  }, [identityReady]);

  useEffect(() => {
    if (fontsReadyState !== 'pending' && fontsReadyAtRef.current === undefined) {
      fontsReadyAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    }
  }, [fontsReadyState]);

  useEffect(() => {
    if (!firstFrameReady || firstFrameCommitted) return;
    setFirstFrameCommitted(true);
  }, [firstFrameCommitted, firstFrameReady]);

  useEffect(() => {
    if (
      !firstFrameCommitted ||
      isEmbedEditorPreview ||
      hasParentOwnedPreview ||
      hasMeasuredFirstFrameRef.current
    ) {
      return;
    }
    hasMeasuredFirstFrameRef.current = true;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const durationMs = Math.max(0, Math.round(now - firstFrameStartRef.current));
    const elapsedTo = (readyAt: number | undefined): number | null =>
      readyAt === undefined ? null : Math.max(0, Math.round(readyAt - firstFrameStartRef.current));
    const stageTimings = {
      bootstrap_ready_ms: elapsedTo(playbackBootstrapReadyAt),
      gameplay_module_ready_ms: elapsedTo(getQuizGameplayModuleReadyAt()),
      progress_ready_ms: elapsedTo(progressReadyAtRef.current),
      identity_ready_ms: elapsedTo(identityReadyAtRef.current),
      fonts_ready_ms: elapsedTo(fontsReadyAtRef.current),
      machine_ready_ms: elapsedTo(machinePlayableReadyAtRef.current),
    };
    if (
      typeof performance !== 'undefined' &&
      typeof performance.mark === 'function' &&
      typeof performance.measure === 'function'
    ) {
      performance.mark('quiz_playable_ready');
      performance.measure('quiz_playable_ready_duration', {
        start: firstFrameStartRef.current,
        end: now,
      });
    }
    trackBreadcrumb('performance', 'quiz_playable_ready', {
      embedded: isEmbeddedQuiz,
      has_email_intent: hasEmailDeepLink,
      font_state: fontsReadyState,
      ...stageTimings,
      duration_ms: durationMs,
    });
    queueGTMEvent({
      event: 'quiz_playable_ready',
      embedded: isEmbeddedQuiz,
      has_email_intent: hasEmailDeepLink,
      font_state: fontsReadyState,
      ...stageTimings,
      duration_ms: durationMs,
    });
  }, [
    firstFrameCommitted,
    fontsReadyState,
    hasParentOwnedPreview,
    hasEmailDeepLink,
    isEmbedEditorPreview,
    isEmbeddedQuiz,
    playbackBootstrapReadyAt,
  ]);

  // Generate quiz schema for structured data
  const quizSchema = useMemo(() => {
    if (!adapter.quiz) return null;
    const fullUrl = getFullUrl(
      typeof window !== 'undefined'
        ? window.location.pathname
        : `/quiz/${encodeURIComponent(adapter.quiz.slug || '')}`
    );

    // Map initial questions to the schema format if available
    const schemaQuestions = initialQuestions?.map((q) => ({
      questionText: q.stem,
      acceptedAnswerText: q.key,
    }));

    return getQuizSchema({
      name: title,
      url: fullUrl,
      description,
      questions: schemaQuestions,
    });
  }, [adapter.quiz, title, description, initialQuestions]);

  if (!adapter.isLoading && !adapter.quiz && !adapter.error) {
    return <NotFoundPage />;
  }

  if (adapter.error && quizQuestions.length === 0) {
    return (
      <div className="w-full">
        <GameplaySection className="flex items-center px-5 py-12">
          <ActionableErrorState
            title="We couldn’t load this quiz"
            message="Reload the quiz to try again, or choose another place to continue."
            actions={[
              {
                label: 'Reload quiz',
                onSelect: () => window.location.reload(),
                emphasis: 'primary',
              },
              ...(isEmbeddedQuiz
                ? [
                    {
                      label: 'Open Atrivial',
                      href: routes.home,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    },
                    {
                      label: 'Contact support',
                      href: routes.contactUs,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    },
                  ]
                : [
                    {
                      label: 'Go home',
                      to: preserveParentManagedIdentityOnInternalTarget(routes.home),
                    },
                    {
                      label: 'Contact support',
                      to: preserveParentManagedIdentityOnInternalTarget(routes.contactUs),
                    },
                  ]),
            ]}
          />
        </GameplaySection>
      </div>
    );
  }

  if (identityResolutionFailed) {
    return (
      <GameplaySection className="flex items-center px-5 py-12">
        <ActionableErrorState
          title="We couldn’t prepare this quiz session"
          message="Reload the quiz to try again. Any saved progress is safe."
          actions={[
            {
              label: 'Reload quiz',
              onSelect: () => window.location.reload(),
              emphasis: 'primary',
            },
          ]}
        />
      </GameplaySection>
    );
  }

  if (progressResolutionFailed || (machinePlayableFailed && !firstFrameCommitted)) {
    return (
      <GameplaySection className="flex items-center px-5 py-12">
        <ActionableErrorState
          title="We couldn’t load your quiz progress"
          message="Reload the quiz to try again. Any progress already saved is safe."
          actions={[
            {
              label: 'Reload quiz',
              onSelect: () => window.location.reload(),
              emphasis: 'primary',
            },
            {
              label: 'Go home',
              to: preserveParentManagedIdentityOnInternalTarget(routes.home),
            },
          ]}
        />
      </GameplaySection>
    );
  }

  return (
    <div className="w-full">
      {quizSchema && <StructuredData data={quizSchema} />}

      <GameplaySection className="quiz-gameplay-section px-5 md:px-10 md:pb-8">
        {presetResolutionFailed ? (
          <ActionableErrorState
            title="We couldn’t load this Quiz’s Sponsors & Style"
            message="Try again to load the presentation preset before playing this Quiz."
            actions={[
              {
                label: 'Try again',
                onSelect: presetSession.retry,
                emphasis: 'primary',
              },
              {
                label: 'Go home',
                to: preserveParentManagedIdentityOnInternalTarget(routes.home),
              },
            ]}
          />
        ) : previewSurface === 'end-screen' ? (
          <Suspense
            fallback={<GameSkeleton mode="quiz" showGenerationTag={false} stablePresentation />}
          >
            <QuizCompletionSummary
              adapterCompletion={endScreenPreviewData.adapterCompletion}
              xpBreakdown={quizCompletionPreviewFixture.xpBreakdown}
              quizTitle={title}
              quizSlug={adapter.quiz?.slug ?? slug}
              quizId={adapter.quiz?.quiz_id ?? adapter.quiz?.id ?? 'preview-quiz-id'}
              quizEditionId={adapter.quiz?.edition_id ?? null}
              quizEditionCreatedAt={adapter.quiz?.created_at ?? null}
              libraryCategorySlugs={adapter.quiz?.library_category_slugs ?? null}
              socialLeaderboardEnabled={adapter.quiz?.social_leaderboard_enabled === true}
              questions={endScreenPreviewData.questions}
              userProgress={endScreenPreviewData.userProgress}
              resolvedEmailSignupContent={resolvedEmailSignupContent}
              resolvedSponsorshipCtaContent={resolvedSponsorshipCtaContent}
              partnerId={adapter.quiz?.partner_id ?? searchParams.get('partner_id') ?? null}
              previewMode={true}
              hasPersistedCompletion={false}
            />
            {notifyPreviewContentReady && (
              <PreviewReadySignal onReady={notifyPreviewContentReady} />
            )}
          </Suspense>
        ) : (
          <>
            {shouldMountGame && (
              <div hidden={!firstFrameCommitted} aria-hidden={!firstFrameCommitted}>
                <Suspense fallback={null}>
                  <QuizGameController
                    adapter={adapter}
                    newsletterInvitation={newsletter.Invitation}
                    newsletterInvitationPlacement={newsletter.placement}
                    emailAnswerCanonicalIndex={emailAnswerCanonicalIndex}
                    emailPollOptionId={emailPollOptionId}
                    showGenerationTag={false}
                    uiConfig={{
                      hideQuizTitle: true,
                      hideVotingButtons: true,
                      nextButtonText: 'Next question',
                    }}
                    sponsorshipContent={resolvedSponsorshipContent}
                    completionEmailSignupContent={resolvedEmailSignupContent}
                    completionSponsorshipCtaContent={resolvedSponsorshipCtaContent}
                    completionPreviewMode={isPreviewMode}
                    sponsorsStyleAttribution={sponsorsStyleAttribution}
                    onPlayableContentReady={handlePlayableContentReady}
                    onPlayableContentError={handlePlayableContentFailed}
                  />
                  {firstFrameCommitted &&
                    hasGenerationPreviewContent &&
                    previewSurface === 'question' &&
                    notifyPreviewContentReady && (
                      <PreviewReadySignal onReady={notifyPreviewContentReady} />
                    )}
                </Suspense>
              </div>
            )}
            {!firstFrameCommitted && (
              <GameSkeleton mode="quiz" showGenerationTag={false} stablePresentation />
            )}
          </>
        )}
      </GameplaySection>
    </div>
  );
};

export default QuizPage;
